import { useState, useEffect, useRef } from "react";
import { Layout } from "antd";
import Sidebar from "../main-layout/Sidebar";
import { Navigate, useLocation } from "react-router-dom";
import { getSessionData } from "../../utils/interceptors/localStorageService";
import { SelfUserClassificationEnum } from "../../shared/constants/constants";
import DashboardHeader from "../../components/Header/admin_header";
import MainLayout from "../main-layout/MainLayout";
import { useMenu } from "../../store/context/admin/menu-context";
import { useTheme } from "../../store/context/theme-context";
import {
  readPermissionFlagsFromCache,
  loadPermissionFlags,
} from "../../utils/helper/permission-toggle";
import { AdminDashboardProvider } from "../../store/context/admin/dashboard-context";
import { LoanRequestProvider } from "../../store/context/admin/loan-request-context";
import { BRANCH_SELECTION_CHANGED_EVENT } from "../../utils/branchSelection";

const { Content, Sider } = Layout;

const AdminLayout = ({ children }) => {
  const sessionData = getSessionData();
  const location = useLocation();
  const { theme } = useTheme();
  const { useV2Design, fetchMenuSchemas, isFetchingMenuSchema, menuList } =
    useMenu();
  const [collapsed, setCollapsed] = useState(window.innerWidth < 768);
  const [branchRefreshKey, setBranchRefreshKey] = useState(0);

  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  const isAuthenticated = !!sessionData;
  const isAdmin =
    sessionData?.userClassification ===
    SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value;

  // Determine if sidebar should be hidden completely
  const shouldHideSidebar = useV2Design && isFetchingMenuSchema && !menuList;

  // Track if we've already attempted to load permissions and menu
  const hasInitializedRef = useRef(false);

  // DashboardHeader is position:fixed, so everything below it has to be offset
  // by its height explicitly (it renders shorter on mobile).
  const headerHeight = isSmallScreen ? 40 : 48;
  const drawerWidth = isSmallScreen
    ? Math.min(300, window.innerWidth - 56)
    : 270;
  // The mobile drawer is never "collapsed" — it slides away at full width, so
  // the menu must keep rendering expanded or it flips to the icon rail
  // mid-animation.
  const sidebarCollapsed = isSmallScreen ? false : collapsed;

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  useEffect(() => {
    // Only run once when component mounts and conditions are met
    if (hasInitializedRef.current || !useV2Design || menuList) {
      return;
    }

    const fetchMenuWithPermissions = async () => {
      hasInitializedRef.current = true;

      // Ensure permissions are loaded before fetching menu schema
      // Note: Permissions are already loaded in OTP.js after login, so we only
      // need to check cache here. If cache exists, use it. If not, it means
      // this is a direct navigation/refresh, so load permissions.
      const cachedPermissions = readPermissionFlagsFromCache();
      console.log("cachedPermissions", cachedPermissions);
      if (!cachedPermissions) {
        // Only load if no cache exists (e.g., direct navigation or page refresh)
        // This prevents double loading after login since OTP.js already loads permissions
        try {
          await loadPermissionFlags({ forceRefresh: false });
          console.log("Permission flags loaded before fetching menu schema");
        } catch (error) {
          console.error("Error loading permission flags:", error);
          // Continue with menu fetch even if permission loading fails
        }
      }
      fetchMenuSchemas();
    };

    fetchMenuWithPermissions();
  }, [menuList, useV2Design, fetchMenuSchemas]);

  useEffect(() => {
    const handleResize = () => {
      const small = window.innerWidth < 768;
      setIsSmallScreen(small);
      setCollapsed(small);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // On mobile the drawer sits over the page, so navigating away has to dismiss
  // it — otherwise you land on the new page without seeing it.
  useEffect(() => {
    if (isSmallScreen) {
      setCollapsed(true);
    }
  }, [location.pathname, location.search, isSmallScreen]);

  useEffect(() => {
    const handleBranchSelectionChanged = () => {
      setBranchRefreshKey((prev) => prev + 1);
    };

    window.addEventListener(
      BRANCH_SELECTION_CHANGED_EVENT,
      handleBranchSelectionChanged,
    );

    return () =>
      window.removeEventListener(
        BRANCH_SELECTION_CHANGED_EVENT,
        handleBranchSelectionChanged,
      );
  }, []);

  if (!isAuthenticated) {
    return <Navigate to="/admin-login" replace />;
  }

  if (!isAdmin) {
    console.log("not an admin, redirecting to dashboard");
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AdminDashboardProvider>
      <LoanRequestProvider>
        <Layout style={{ minHeight: "100vh" }}>
          <DashboardHeader
            collapsed={collapsed}
            onMenuClick={toggleCollapsed}
          />

          <Layout hasSider>
            {!shouldHideSidebar && (
              <Sider
                // On mobile the drawer keeps its full width and slides out of view.
                // Letting antd animate the width instead reflows the whole menu on
                // every frame, which is what makes the open/close flicker.
                collapsed={sidebarCollapsed}
                onCollapse={setCollapsed}
                width={drawerWidth}
                collapsedWidth={isSmallScreen ? 0 : 70}
                style={{
                  overflow: "hidden",
                  height: `calc(100vh - ${headerHeight}px)`,
                  position: "fixed",
                  left: 0,
                  top: headerHeight,
                  bottom: 0,
                  zIndex: 100,
                  ...(isSmallScreen
                    ? {
                        width: drawerWidth,
                        maxWidth: drawerWidth,
                        minWidth: drawerWidth,
                        transform: collapsed
                          ? `translate3d(-${drawerWidth}px, 0, 0)`
                          : "translate3d(0, 0, 0)",
                        transition:
                          "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                        willChange: "transform",
                        boxShadow: collapsed
                          ? "none"
                          : "2px 0 8px rgba(0,0,0,0.15)",
                      }
                    : null),
                }}
                breakpoint="md"
                onBreakpoint={setCollapsed}
              >
                {isAdmin && useV2Design ? (
                  <MainLayout
                    collapsed={sidebarCollapsed}
                    setCollapsed={setCollapsed}
                    type="inner"
                  />
                ) : (
                  <Sidebar collapsed={sidebarCollapsed} type="inner" />
                )}
              </Sider>
            )}

            {isSmallScreen && !shouldHideSidebar && (
              <div
                style={{
                  position: "fixed",
                  top: headerHeight,
                  left: 0,
                  width: "100%",
                  height: `calc(100% - ${headerHeight}px)`,
                  background: "rgba(0, 0, 0, 0.5)",
                  zIndex: 90,
                  opacity: collapsed ? 0 : 1,
                  pointerEvents: collapsed ? "none" : "auto",
                  transition: "opacity 0.25s ease",
                }}
                onClick={toggleCollapsed}
              />
            )}

            <Layout
              style={{
                marginLeft:
                  shouldHideSidebar || isSmallScreen ? 0 : collapsed ? 70 : 270,
                marginTop: headerHeight,
                transition: "margin-left 0.2s ease",
                minHeight: `calc(100vh - ${headerHeight}px)`,
                minWidth: 0,
              }}
            >
              <Content
                style={{
                  overflow: "auto",
                  padding: isSmallScreen ? 10 : "24px",
                  backgroundColor: theme === "dark" ? "#111111" : "#fdfdfd",
                }}
              >
                <div
                  key={`${location.pathname}${location.search}:${branchRefreshKey}`}
                  className="min-h-[100vh] h-auto"
                >
                  {children}
                </div>
              </Content>
            </Layout>
          </Layout>
        </Layout>
      </LoanRequestProvider>
    </AdminDashboardProvider>
  );
};

export default AdminLayout;
